import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import Portal, { type RootId } from '~/components/molecule/Portal';
import getClosestScrollableParent from '~/util/getClosestScrollableParent';
import calculateListPosition from './utils/calculateListPosition';

export type Props = {
  openerRef: React.RefObject<HTMLElement> | null;
  rootId: RootId;
  isOpen: boolean;
};

export type ListSize = {
  width: number;
  height: number;
};

const PortalledPositioner: React.FCC<Props> = ({
  dataTestId,
  /**
   * Whether the portal is open, after it is opened we will calculate the position
   * and set the transform state
   */
  isOpen,

  /**
   * Ref to the component that opened the list
   */
  openerRef,

  /**
   * Portal root id
   */
  rootId,

  /**
   * Children to display in the portal
   */
  children,
}) => {
  const portalInnerRef = useRef<HTMLDivElement>(null);
  const [measuredListSize, setMeasuredListSize] = useState<ListSize>({
    width: 0,
    height: 0,
  });
  const [transform, setTransform] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  const measureAndPosition = useCallback(() => {
    const element = portalInnerRef.current;
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const nextSize = { width: rect.width, height: rect.height };

    setMeasuredListSize(nextSize);

    const currentOpenerRect = openerRef?.current?.getBoundingClientRect();

    const { newX, newY } = calculateListPosition({
      openerRect: currentOpenerRect || null,
      listSize: nextSize,
    });

    setTransform({ x: newX, y: newY });
  }, [openerRef]);

  // Handle size changes of the list
  useLayoutEffect(() => {
    if (!isOpen) return;

    const element = portalInnerRef.current;
    if (!element) return;

    measureAndPosition();
    const observer = new ResizeObserver(measureAndPosition);
    observer.observe(element);

    return () => observer.disconnect();
  }, [isOpen, measureAndPosition]);

  // Handle resize and scroll events
  useEffect(() => {
    if (!isOpen) return;

    measureAndPosition();

    window.addEventListener('resize', measureAndPosition);
    window.addEventListener('scroll', measureAndPosition);

    const scrollableParent = openerRef
      ? getClosestScrollableParent(openerRef.current)
      : null;

    if (scrollableParent) {
      scrollableParent.addEventListener('scroll', measureAndPosition);
    }

    return () => {
      window.removeEventListener('resize', measureAndPosition);
      window.removeEventListener('scroll', measureAndPosition);

      if (scrollableParent) {
        scrollableParent.removeEventListener('scroll', measureAndPosition);
      }
    };
  }, [isOpen, measureAndPosition, openerRef]);

  if (!isOpen) return null;

  const isMeasuring =
    measuredListSize.width === 0 || measuredListSize.height === 0;

  return (
    <Portal root={rootId}>
      <PortalInner
        ref={portalInnerRef}
        $top={isMeasuring ? 0 : transform.y}
        $left={isMeasuring ? 0 : transform.x}
        $isMeasuring={isMeasuring}
        data-testid={dataTestId}
      >
        {children}
      </PortalInner>
    </Portal>
  );
};

const PortalInner = styled.div<{
  $top: number;
  $left: number;
  $isMeasuring: boolean;
}>(
  ({ $top, $left, $isMeasuring }) => css`
    position: absolute;
    top: ${$top || 0}px;
    left: ${$left || 0}px;
    ${$isMeasuring &&
    css`
      visibility: hidden;
    `}
  `,
);

export default PortalledPositioner;
